import { inject, Injectable, signal } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { Language } from '@core/models/language';
import { map, Observable, of } from 'rxjs';
import { ApplicationsListService } from './applications-list.service';

@Injectable({
    providedIn: 'root',
})
export class ApplicationRedirectionService {
    private router = inject(Router);
    private applicationsList = inject(ApplicationsListService);

    public isRedirectingToExternalUrl = signal(false);

    /**
     * redirect to the returnUrl or to the application selection page if the returnUrl is not defined.
     * @param returnUrl Optional. Replacement of the originUrl query string parameter to handle cases where it is not yet defined (guards).
     * @param resource Optional. Replacement of the resource query string parameter to handle cases where it is not yet defined (guards).
     */
    redirect$(returnUrl: string | null, resourceType: string | null, language: Language, noRedirect = false): Observable<UrlTree> {
        if (this.isRedirectingToExternalUrl()) {
            return of(this.router.createUrlTree(['redirecting']));
        }
        if (!noRedirect) {
            if (resourceType) {
                return this.applicationsList.getAccessibleApplication(resourceType).pipe(
                    // If an application is found, it means that it exists and that the user has access to it.
                    map((application) => {
                        if (application) {
                            const url = returnUrl ? decodeURIComponent(returnUrl) : application.urls[language];
                            return this.redirectToToExternalUrl(url);
                        } else {
                            return this.getApplicationSelectionUrl(noRedirect, resourceType);
                        }
                    }),
                );
            } else if (!resourceType && returnUrl && this.returnUrlIsExternal(returnUrl)) {
                return of(this.redirectToToExternalUrl(decodeURIComponent(returnUrl)));
            }
        }
        return of(this.getApplicationSelectionUrl(noRedirect, resourceType));
    }

    private redirectToToExternalUrl(returnUrl: string): UrlTree {
        window.location.href = returnUrl;
        this.isRedirectingToExternalUrl.set(true);
        return this.router.createUrlTree(['redirecting']);
    }

    private getApplicationSelectionUrl(noRedirect: boolean, resourceType?: string | null): UrlTree {
        return this.router.createUrlTree(['application-selection'], {
            queryParams: { resourceType, noRedirect },
            queryParamsHandling: 'merge',
        });
    }

    private returnUrlIsExternal(returnUrl: string): boolean {
        return returnUrl !== window.location.origin;
    }
}
